/**
 * API-Client: JSON, CSRF-Token (Header), stabile Fehlercodes.
 * Hintergrund-Abfragen (Polling) tragen `X-Omony-Background: 1` und verlängern die Sitzung nicht.
 */
let csrfToken = "";
export const setCsrfToken = (t: string | null | undefined) => {
  csrfToken = t ?? "";
};

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    readonly extra: Record<string, unknown> = {},
  ) {
    super(code);
  }
}

interface Options {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  background?: boolean;
}

export async function api<T = unknown>(path: string, opts: Options = {}): Promise<T> {
  const method = opts.method ?? "GET";
  const headers: Record<string, string> = { Accept: "application/json" };
  if (opts.body !== undefined) headers["Content-Type"] = "application/json";
  if (method !== "GET") headers["X-CSRF-Token"] = csrfToken;
  if (opts.background) headers["X-Omony-Background"] = "1";
  let res: Response;
  try {
    res = await fetch(`/api/v1${path}`, {
      method,
      headers,
      body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
      credentials: "same-origin",
      cache: "no-store",
    });
  } catch {
    throw new ApiError(0, "network");
  }
  if (res.status === 204) return undefined as T;
  const data = (await res.json().catch(() => ({}))) as {
    error?: { code?: string } & Record<string, unknown>;
  };
  if (!res.ok) {
    const { code, ...extra } = data.error ?? {};
    // Sitzung abgelaufen/gesperrt: Oberfläche sofort in den passenden Zustand bringen
    if (res.status === 401 && !path.startsWith("/auth/"))
      window.dispatchEvent(new Event("omony:unauthenticated"));
    throw new ApiError(res.status, code ?? "internal", extra);
  }
  return data as T;
}

/** i18n-Schlüssel für einen Fehler (bekannte Codes, sonst allgemein). */
const KNOWN = new Set([
  "login_failed",
  "mfa_invalid",
  "mfa_locked",
  "password_invalid",
  "token_invalid",
  "email_exists",
  "last_admin",
  "cannot_change_self",
  "pin_format",
  "pin_trivial",
  "pin_invalid",
  "pin_locked",
  "name_exists",
  "validation",
  "rate_limited",
  "forbidden",
  "network",
  "code_invalid",
  "csrf_invalid",
  "not_found",
  "case_not_found",
  "version_conflict",
  "transition_not_allowed",
  "patient_invalid",
  "patient_missing",
  "patient_not_confirmed",
  "kvnr_mismatch",
  "confirmation_required",
  "selbstauskunft_required",
  "vital_invalid",
  "video_not_available",
  "smed_voucher_failed",
  "smed_result_missing",
  "smed_disabled",
  "billing_not_open",
  "reason_invalid",
  "pin_change_required",
  "pin_unchanged",
  "ocr_failed",
  "ocr_refused",
  "bad_image",
  "unauthenticated",
  "device_not_paired",
  "room_create_failed",
]);

export function errorMessageId(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.code === "password_policy") return `error.password.${String(e.extra.reason ?? "too_short")}`;
    if (KNOWN.has(e.code)) return `error.${e.code}`;
  }
  return "error.generic.text";
}
